import { BREADCRUMBS } from 'breadcrumbs/constants/breadcrumbs';
import { getBreadcrumbs } from 'server/utils/breadcrumbs/getBreadcrumbs';
import { fetchGlobalPricingId } from 'server/utils/fetchGlobalPricingId';
import { IProductLandingPage } from 'shared/types/ProductLandingPage';
import { PlpPageComponent as PLPLandingPageComponent } from 'product-landing-pages/components/PlpPageComponent';
import { startCase } from 'shared/utils/startCase';
import { getBrowseResults } from 'server/utils/constructor/getBrowseResults';
import { isSearchCrawler } from 'shared/utils/isSearchCrawler';
import { t } from 'localization/utils/t';
import { setServerSideTranslations } from 'localization/utils/setServerSideTranslations';
import { FeatureFlags } from 'featureFlags/constants';
import { getConsumerSearchServerSideData } from 'server/utils/getConsumerSearchDataServerSide';
import QueryString from 'qs';
import {
  CONSUMER_SEARCH_SILHOUETTES_REDIRECTS,
  CONSUMER_SEARCH_SILHOUETTES_REVERSE_REDIRECTS,
} from 'brand/enum/consumerSearchRedirects';
import { getRedirectionPath } from 'localization/utils/getRedirectionPath';
import { slugify } from 'shared/utils/consumerSearchSlugify';

const TP = 'pages.pageSlug.silhouette.silhouette';

const SilhouettePage = ({
  brandDisplayName,
  brandName,
  breadcrumbs,
  browseKey,
  browseValue,
  category,
  constructorResponse,
  consumerSearchFilters,
  displayName,
  globalPricingId,
  silhouette,
  isCrawler = false,
}: IProductLandingPage) => {
  const {
    name = displayName,
    description = '',
    metadataDescription = null,
    titleTag = '',
  } = breadcrumbs || {};

  return (
    <PLPLandingPageComponent
      brandName={brandName}
      breadcrumbs={[
        { name: startCase(category), slug: `/${category}` },
        {
          name: brandDisplayName,
          slug: `/${category}/brand/${slugify(brandName)}`,
        },
        {
          name: startCase(silhouette),
          slug: `/${category}/silhouette/${slugify(silhouette)}`,
          isStatic: true,
        },
      ]}
      browseKey={browseKey}
      browseValue={browseValue}
      category={category}
      constructorResponse={constructorResponse}
      consumerSearchFilters={consumerSearchFilters}
      description={description}
      displayName={name}
      globalPricingId={globalPricingId}
      hasBreadcrumbSchema
      metadataDescription={metadataDescription}
      metaTitle={titleTag}
      silhouette={silhouette}
      isCrawler={isCrawler}
    />
  );
};

export const getServerSideProps = async ({ query, resolvedUrl, req, res, locale }) => {
  const { pageSlug: category, silhouette }: { pageSlug: string; silhouette: string } = query;
  const q = QueryString.parse(query);
  const globalPricingId = await fetchGlobalPricingId({ req, res });
  const breadcrumbs = await getBreadcrumbs({
    req,
    res,
    path: `${BREADCRUMBS.SILHOUETTE}/${CONSUMER_SEARCH_SILHOUETTES_REVERSE_REDIRECTS[silhouette] || silhouette}`,
  });
  const isConsumerSearchEnabled =
    req.featureFlags?.[FeatureFlags.TEMP_WEB_ENABLE_SEARCH_MICROFRONTEND];

  const browseName: string = startCase(silhouette);
  const displayName = t(`${TP}.displayName`, '{browseName} Sneakers', { browseName });
  const isCrawler = isSearchCrawler(req?.headers);

  const consumerSearchFilters = {
    silhouettes: silhouette,
    categories: 'footwear',
  };

  if (isConsumerSearchEnabled) {
    if (CONSUMER_SEARCH_SILHOUETTES_REDIRECTS[silhouette]) {
      return {
        redirect: {
          destination: getRedirectionPath(
            `/${category}/silhouette/${CONSUMER_SEARCH_SILHOUETTES_REDIRECTS[silhouette]}`,
            locale,
          ),
          permanent: false,
        },
      };
    }
    // If consumer search is enabled, we need to fetch the consumer data details
    const consumerSearchInitialData = await getConsumerSearchServerSideData({
      filters: { ...q, pageFilters: consumerSearchFilters },
      isCrawler,
    });

    if (!consumerSearchInitialData?.items?.hasData) {
      return { notFound: true };
    }

    const brandsList = consumerSearchInitialData?.aggregations?.list?.find(
      (a) => a.bucketName === 'brands',
    );
    const brandWithHighestCount = brandsList?.valuesList?.reduce((max, current) =>
      current.count > max.count ? current : max,
    );

    return {
      props: {
        ...(await setServerSideTranslations({ locale })),
        brandName: brandWithHighestCount?.slug || '',
        brandDisplayName: brandWithHighestCount?.value || '',
        breadcrumbs,
        browseKey: 'silhouette',
        browseValue: browseName?.toLowerCase(),
        category,
        constructorResponse: {},
        displayName,
        globalPricingId,
        silhouette,
        consumerSearchFilters,
        consumerSearchInitialData: JSON.stringify({}),
        isCrawler,
      },
    };
  }

  try {
    const browseResponse = await getBrowseResults({
      req,
      res,
      query,
      resolvedUrl,
      filterKey: 'silhouette',
      filterValue: browseName?.toLowerCase(),
    });
    const invalidBrowse = browseResponse.response.total_num_results === 0;

    if (invalidBrowse) {
      return {
        notFound: true,
      };
    }

    const brandFacet = browseResponse?.response?.facets?.filter((facet) => facet?.name === 'brand');
    const brandName = brandFacet?.[0]?.options?.[0]?.value;
    const brandDisplayName: string = brandFacet?.[0]?.options?.[0]?.display_name || '';

    return {
      props: {
        ...(await setServerSideTranslations({ locale })),
        brandName,
        brandDisplayName,
        breadcrumbs,
        browseKey: 'silhouette',
        browseValue: browseName?.toLowerCase(),
        category,
        constructorResponse: browseResponse,
        displayName,
        globalPricingId,
        silhouette,
        consumerSearchFilters,
        isCrawler,
      },
    };
  } catch {
    return {
      notFound: true,
    };
  }
};

export default SilhouettePage;
